import { CheckOutlined } from "@ant-design/icons";
import { message } from "antd";
import { scenarioApi } from "api/scenario.api";
import { AppButton } from "components/antd-ui";
import { useScenario } from "hooks/useScenario";
import { useDebounce } from "hooks/useDebounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getTitle } from "utils";
import { ScenarioEditor } from "./components/ScenarioEditor";
import { ScenarioList } from "./components/ScenarioList";
import { ScenarioModal } from "./components/ScenarioModal";
import { resolveScenarioId } from "./utils";
import "./ScenarioPage.scss";

export const ScenarioPage = ({ title = "" }) => {
  const [selectedScenarioId, setSelectedScenarioId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const scenarioModalRef = useRef<ScenarioModal>(null);

  const {
    scenarios,
    totalScenario,
    fetchScenario,
    loadingScenario,
    setQueryScenario,
    queryScenario,
  } = useScenario({
    initQuery: {
      page: 1,
      limit: 50,
      search: "",
    },
  });

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  useEffect(() => {
    setQueryScenario((prev) => ({ ...prev, search: debouncedSearch, page: 1 }));
  }, [debouncedSearch, setQueryScenario]);

  useEffect(() => {
    fetchScenario();
  }, [queryScenario]);

  useEffect(() => {
    setSelectedScenarioId((prev) => resolveScenarioId(prev, scenarios));
  }, [scenarios]);

  const selectedScenario = useMemo(
    () => scenarios.find((scenario) => scenario.id === selectedScenarioId) ?? null,
    [scenarios, selectedScenarioId],
  );

  const handleAddScenario = useCallback(() => {
    scenarioModalRef.current?.handleCreate();
  }, []);

  const handleDeleteScenario = useCallback(
    async (id: number) => {
      try {
        await scenarioApi.delete(id);
        message.success("Xóa thành công");
        fetchScenario();
      } catch (error) {
        console.error(error);
      }
    },
    [fetchScenario],
  );

  const handlePublish = useCallback(() => {
    if (!selectedScenario) {
      return;
    }

    message.success(`Đã xuất bản tình huống “${selectedScenario.name}”`);
  }, [selectedScenario]);

  return (
    <div className="scenarioPage">
      <header className="scenarioPageHead">
        <div>
          <h1 className="scenarioPageTitle">Quản lý tình huống</h1>
          <p className="scenarioPageDesc">Tạo luồng phản hồi có kiểm soát — không cần mô hình AI.</p>
        </div>
        <AppButton tone="primary" icon={<CheckOutlined />} onClick={handlePublish}>
          Xuất bản thay đổi
        </AppButton>
      </header>

      <div className="scenarioPageGrid">
        <ScenarioList
          scenarios={scenarios}
          selectedId={selectedScenarioId}
          search={search}
          total={totalScenario}
          loading={loadingScenario}
          onSearchChange={setSearch}
          onSelect={setSelectedScenarioId}
          onAdd={handleAddScenario}
          onDelete={handleDeleteScenario}
        />

        {selectedScenario ? (
          <ScenarioEditor key={selectedScenario.id} scenario={selectedScenario} />
        ) : null}
      </div>

      <ScenarioModal ref={scenarioModalRef} onSubmitOk={fetchScenario} />
    </div>
  );
};
